import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import { useTranslation } from "react-i18next";
import { useInfiniteQuery } from "@tanstack/react-query";
import {
  Box,
  Button,
  Skeleton,
  Stack,
  Typography,
  TextField,
  MenuItem,
  Tooltip,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import DownloadIcon from "@mui/icons-material/Download";

import {
  getMunicipalityProperties,
  downloadMunicipalityPropertiesExport,
} from "../../../shared/api";
import PropertyCard from "./propertyCard";
import AdvancedFiltersModal from "./AdvancedFiltersModal";
import { useClient } from "../../../contexts/ClientContext";
import { isPortfolioIdRequiredError } from "./portfolioApiError";

const defaultFilters = {
  contractPriceFrom: "any",
  contractPriceTo: "any",
  capitalAtRiskFrom: "any",
  capitalAtRiskTo: "any",
  contentsCapitalAtRiskFrom: "any",
  contentsCapitalAtRiskTo: "any",
  fireRiskRange: [0, 1],
  floodRiskRange: [0, 1],
  earthquakeRiskRange: [0, 1],
  certainAddress: "all",
  earthquakeCoverage: "all",
};

function AllPropertiesView({
  mapRef,
  setMarkerInfo,
  setCurrentProperties,
  selectedPortfolioId,
}) {
  const { t } = useTranslation();
  const { clientInfo } = useClient();
  const loaderRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const [orderCombined, setOrderCombined] = useState("capitalAtRisk:desc");
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filters, setFilters] = useState(defaultFilters);
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Debounce search input separately from filters
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchInput]);

  // Memoized common API parameters to avoid duplication
  const commonApiParams = useMemo(() => {
    const [orderBy, orderDir] = orderCombined.split(":");
    return {
      search: debouncedSearch || undefined,
      orderBy,
      orderDir,
      contractPriceMin:
        filters.contractPriceFrom === "any"
          ? undefined
          : filters.contractPriceFrom,
      contractPriceMax:
        filters.contractPriceTo === "any" ? undefined : filters.contractPriceTo,
      capitalAtRiskMin:
        filters.capitalAtRiskFrom === "any"
          ? undefined
          : filters.capitalAtRiskFrom,
      capitalAtRiskMax:
        filters.capitalAtRiskTo === "any" ? undefined : filters.capitalAtRiskTo,
      contentsCapitalAtRiskMin:
        filters.contentsCapitalAtRiskFrom === "any"
          ? undefined
          : filters.contentsCapitalAtRiskFrom,
      contentsCapitalAtRiskMax:
        filters.contentsCapitalAtRiskTo === "any"
          ? undefined
          : filters.contentsCapitalAtRiskTo,
      fireRiskMin: filters.fireRiskRange[0],
      fireRiskMax: filters.fireRiskRange[1],
      floodRiskMin: filters.floodRiskRange[0],
      floodRiskMax: filters.floodRiskRange[1],
      earthquakeRiskMin: filters.earthquakeRiskRange[0],
      earthquakeRiskMax: filters.earthquakeRiskRange[1],
      certainAddress:
        filters.certainAddress === "all"
          ? undefined
          : filters.certainAddress === "yes"
            ? true
            : false,
      earthquakeCoverage:
        filters.earthquakeCoverage === "all"
          ? undefined
          : filters.earthquakeCoverage === "yes"
            ? true
            : false,
      clientId: clientInfo?.clientId,
      portfolioId: selectedPortfolioId || undefined,
    };
  }, [
    orderCombined,
    debouncedSearch,
    filters,
    clientInfo?.clientId,
    selectedPortfolioId,
  ]);

  const fetchPage = useCallback(
    async ({ pageParam = 1 }) => {
      const res = await getMunicipalityProperties({
        ...commonApiParams,
        page: pageParam,
        limit: 30,
      });

      if (Array.isArray(res))
        return {
          items: res,
          page: pageParam,
          totalPages: 1,
          total: res.length,
        };
      return {
        items: res.items ?? [],
        page: res.page ?? pageParam,
        totalPages: res.totalPages ?? 1,
        total: res.total ?? 0,
      };
    },
    [commonApiParams],
  );

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
    error,
  } = useInfiniteQuery({
    queryKey: [
      "allProperties",
      {
        order: orderCombined,
        search: debouncedSearch,
        filters,
        portfolioId: selectedPortfolioId,
      },
    ],
    queryFn: fetchPage,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page, totalPages } = lastPage;
      return page < totalPages ? page + 1 : undefined;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes - data is considered fresh for 2 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes - keep in cache for 15 minutes
    enabled: !!clientInfo?.clientId && !!selectedPortfolioId,
  });

  useEffect(() => {
    if (!loaderRef.current) return;
    if (!hasNextPage || isFetchingNextPage) return;

    const node = loaderRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root: null, rootMargin: "200px", threshold: 0 },
    );
    observer.observe(node);
    return () => observer.unobserve(node);
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const items = useMemo(
    () => (data?.pages ?? []).flatMap((p) => p.items ?? []),
    [data],
  );

  const totalCount = useMemo(() => {
    return data?.pages?.[0]?.total ?? 0;
  }, [data]);

  // Fetch all properties for map markers (without limit)
  const fetchAllPropertiesForMap = useCallback(async () => {
    if (!clientInfo?.clientId) return [];

    try {
      const res = await getMunicipalityProperties({
        ...commonApiParams,
        page: 1,
        limit: undefined, // No limit for map markers
      });

      if (Array.isArray(res)) {
        return res;
      }
      return res.items ?? [];
    } catch (error) {
      console.error("Error fetching all properties for map:", error);
      return [];
    }
  }, [commonApiParams, clientInfo?.clientId]);

  // Update current properties for map markers with all properties
  useEffect(() => {
    const updateMapProperties = async () => {
      const allProperties = await fetchAllPropertiesForMap();
      setCurrentProperties?.(allProperties);
    };

    updateMapProperties();
  }, [fetchAllPropertiesForMap, setCurrentProperties]);

  const handleCardClick = useCallback(
    (property) => {
      setMarkerInfo?.(property);
      if (mapRef?.current && property?.lng != null && property?.lat != null) {
        try {
          mapRef.current.flyTo({
            center: [Number(property.lng), Number(property.lat)],
            // zoom: 14,
            essential: true,
          });
        } catch {}
      }
    },
    [mapRef, setMarkerInfo],
  );

  const handleFiltersChange = useCallback((newFilters) => {
    setFilters(newFilters);
  }, []);

  // Memoize the search input change handler specifically
  const handleSearchInputChange = useCallback((e) => {
    setSearchInput(e.target.value);
  }, []);

  const handleOrderChange = useCallback((e) => {
    setOrderCombined(e.target.value);
  }, []);

  const orderOptions = useMemo(
    () => [
      {
        value: "capitalAtRisk:desc",
        label: t("portfolioMap.order.capitalAtRiskDesc"),
      },
      {
        value: "capitalAtRisk:asc",
        label: t("portfolioMap.order.capitalAtRiskAsc"),
      },
      {
        value: "contentsCapitalAtRisk:desc",
        label: t("portfolioMap.order.contentsCapitalAtRiskDesc"),
      },
      {
        value: "contentsCapitalAtRisk:asc",
        label: t("portfolioMap.order.contentsCapitalAtRiskAsc"),
      },
      {
        value: "contractPrice:desc",
        label: t("portfolioMap.order.contractPriceDesc"),
      },
      {
        value: "contractPrice:asc",
        label: t("portfolioMap.order.contractPriceAsc"),
      },
      { value: "fireRisk:desc", label: t("portfolioMap.order.fireRiskDesc") },
      { value: "fireRisk:asc", label: t("portfolioMap.order.fireRiskAsc") },
      { value: "floodRisk:desc", label: t("portfolioMap.order.floodRiskDesc") },
      { value: "floodRisk:asc", label: t("portfolioMap.order.floodRiskAsc") },
      {
        value: "earthquakeRisk:desc",
        label: t("portfolioMap.order.earthquakeRiskDesc"),
      },
      {
        value: "earthquakeRisk:asc",
        label: t("portfolioMap.order.earthquakeRiskAsc"),
      },
    ],
    [t],
  );

  const handleExport = useCallback(async () => {
    if (!clientInfo?.clientId || isExporting) return;

    setIsExporting(true);
    try {
      await downloadMunicipalityPropertiesExport({
        ...commonApiParams,
      });
    } catch (error) {
      console.error("Failed to export properties:", error);
      alert(t("portfolioMap.errors.exportFailed"));
    } finally {
      setIsExporting(false);
    }
  }, [clientInfo?.clientId, commonApiParams, isExporting, t]);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.certainAddress !== "all") count++;
    if (filters.earthquakeCoverage !== "all") count++;
    if (filters.contractPriceFrom !== "any") count++;
    if (filters.contractPriceTo !== "any") count++;
    if (filters.capitalAtRiskFrom !== "any") count++;
    if (filters.capitalAtRiskTo !== "any") count++;
    if (filters.contentsCapitalAtRiskFrom !== "any") count++;
    if (filters.contentsCapitalAtRiskTo !== "any") count++;
    if (filters.fireRiskRange[0] > 0 || filters.fireRiskRange[1] < 1) count++;
    if (filters.floodRiskRange[0] > 0 || filters.floodRiskRange[1] < 1) count++;
    if (
      filters.earthquakeRiskRange[0] > 0 ||
      filters.earthquakeRiskRange[1] < 1
    )
      count++;
    return count;
  }, [filters]);

  if (!selectedPortfolioId) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("portfolioMap.noPortfolioFound", {
          defaultValue: "No portfolio found for this client yet.",
        })}
      </Typography>
    );
  }

  return (
    <>
      <Stack direction="column" spacing={2} sx={{ mb: 2 }}>
        {/* Search Field */}
        <TextField
          fullWidth
          size="small"
          placeholder={t("portfolioMap.searchPlaceholder")}
          value={searchInput}
          onChange={handleSearchInputChange}
        />

        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            select
            fullWidth
            size="small"
            label={t("common.order")}
            value={orderCombined}
            onChange={handleOrderChange}
          >
            {orderOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            sx={{ minWidth: "140px" }}
            onClick={() => setFiltersModalOpen(true)}
          >
            {t("portfolioMap.filters")}
            {activeFiltersCount > 0 && ` (${activeFiltersCount})`}
          </Button>
        </Stack>
      </Stack>

      {/* Total Count Display with Export Button */}
      {!isLoading && totalCount > 0 && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Typography variant="body2" color="text.secondary">
            {t("portfolioMap.resultsCount", { count: totalCount })}
          </Typography>
          <Tooltip title={t("portfolioMap.exportTooltip")}>
            <span>
              <Button
                variant="text"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleExport}
                disabled={isExporting}
              >
                {isExporting
                  ? t("portfolioMap.exporting")
                  : t("portfolioMap.export")}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      )}

      {error ? (
        <Stack gap={1}>
          <Typography color="error">
            {isPortfolioIdRequiredError(error)
              ? t("portfolioMap.errors.portfolioRequired", {
                  defaultValue:
                    "A valid portfolio is required. Please select a completed portfolio.",
                })
              : t("portfolioMap.errors.loadFailed")}
          </Typography>
          <Button variant="contained" onClick={() => refetch()}>
            {t("common.tryAgain")}
          </Button>
        </Stack>
      ) : (
        <>
          <Stack gap={2}>
            {isLoading &&
              Array.from({ length: 6 }).map((_, i) => (
                <Box
                  key={`sk-${i}`}
                  p={1}
                  borderRadius={2}
                  border="1px solid #eee"
                >
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="40%" />
                  <Skeleton variant="rectangular" height={64} />
                </Box>
              ))}

            {items.map((p, idx) => (
              <Box key={p.id ?? `${p.address}-${idx}`}>
                <PropertyCard
                  {...p}
                  showDescriptionsIcon
                  address={p.address}
                  capitalAtRisk={p.capitalAtRisk}
                  contractPrice={p.contractPrice}
                  earthquakeRisk={p.earthquakeRisk}
                  fireRisk={p.fireRisk}
                  floodRisk={p.floodRisk}
                  fireBin={p.fireBin}
                  floodBin={p.floodBin}
                  earthquakeBin={p.earthquakeBin}
                  onClick={() => handleCardClick(p)}
                />
              </Box>
            ))}

            {isFetchingNextPage &&
              Array.from({ length: 6 }).map((_, i) => (
                <Box
                  key={`sk-${i}`}
                  p={1}
                  borderRadius={2}
                  border="1px solid #eee"
                >
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="40%" />
                  <Skeleton variant="rectangular" height={64} />
                </Box>
              ))}

            {!isLoading && items.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                {t("portfolioMap.emptyState")}
              </Typography>
            )}
          </Stack>

          {/* Infinite-scroll sentinel */}
          {hasNextPage && <Box ref={loaderRef} sx={{ height: 1 }} />}
        </>
      )}

      <AdvancedFiltersModal
        open={filtersModalOpen}
        onClose={() => setFiltersModalOpen(false)}
        filters={filters}
        onFiltersChange={handleFiltersChange}
      />
    </>
  );
}

export default AllPropertiesView;
